<template>
  <div class="certificate-page">
   
    <div v-if="showCertificate" :class="['certificate-container', { 'has-background': showCertificate }]" >
      <div v-if="isLoading" class="loading-container">
        <p>加载中，请稍候...</p>
      </div>
      <div v-else class="certificate-content">
        <header class="certificate-header">
          <h1>数据确权证书</h1>
        </header>
        <section class="certificate-body">
          <div class="info-grid">
            <div class="info-item">
              <span class="label">证书编号:</span>
              <span class="value key-value copyable" @click="copyToClipboard(certificateData.evidenceId)">{{ certificateData.evidenceId }}</span>
            </div>
            <div class="info-item">
              <span class="label">权利人:</span>
              <span class="value key-value copyable" @click="copyToClipboard(certificateData.submitter)">{{ certificateData.submitter }}</span>
            </div>
            <div class="info-item full-width">
              <span class="label">数据类型:</span>
              <span class="value key-value">{{ certificateData.dataType }}</span>
            </div>
            <div class="info-item full-width">
              <span class="label">数据指纹:</span>
              <span class="value key-value copyable" @click="copyToClipboard(certificateData.dataHash)">{{ certificateData.dataHash }}</span>
            </div>
            <div class="info-item full-width">
              <span class="label">数据摘要:</span>
              <span class="value value-metadata">{{ certificateData.metadata }}</span>
            </div>
            <div class="info-item">
              <span class="label">区块高度:</span>
              <span class="value key-value copyable" @click="copyToClipboard(certificateData.blockHeight)">{{ certificateData.blockHeight }}</span>
            </div>
            <div class="info-item">
              <span class="label">证书状态:</span>
              <span :class="['value', certificateData.isRevoked ? 'status-revoked' : 'status-normal']">
                <span v-if="certificateData.isRevoked" class="status-icon">❌</span>
                <span v-else class="status-icon">✔️</span>
                {{ certificateData.isRevoked ? '已撤销' : '正常' }}
              </span>
            </div>
            <div class="info-item">
              <span class="label">确权时间:</span>
              <span class="value key-value">{{ formatDate(certificateData.timestamp) }}</span>
            </div>
          </div>
        </section>
        <footer class="certificate-footer">
          <RedSeal :company-name="certificateSeal" :date="formatDate(certificateData.timestamp, 'yyyy-MM-dd')" :size="dynamicRedSealSize" class="certificate-seal" />
          <div class="footer-content-wrapper">
            <qrcode-vue :value="currentCertificateUrl" :size="dynamicQrCodeSize" level="H" class="footer-qr" v-if="certificateData.evidenceId" />
            <p class="footer-text">本证书信息已上链存证，最终解释权归授权方所有。</p>
          </div>
        </footer>
      </div>
    </div>
    
    <div v-else-if="isLoading" class="loading-container">
      <p>加载中，请稍候...</p>
    </div>
    
    <div v-else class="main-query-container">
      <div class="query-portal">
        <h2 class="query-title">数据确权证书查询</h2>
        <p class="query-description">请输入合约地址和数据哈希值以查询对应的确权证书</p>
        
        <div class="error-message" v-if="errorMsg">
          <span class="error-icon">❌</span> {{ errorMsg }}
        </div>
        
        <div class="form-group">
          <label for="contractAddress">确权合约地址</label>
          <input 
            type="text" 
            id="contractAddress" 
            v-model="contractAddressInput" 
            placeholder="请输入链上确权合约地址，例如：0x1234..."
            class="form-control"
          />
        </div>
        
        <div class="form-group">
          <label for="dataHash">数据哈希值</label>
          <input 
            type="text" 
            id="dataHash" 
            v-model="dataHashInput" 
            placeholder="请输入数据哈希值，例如：0xabcd..."
            class="form-control"
          />
        </div>
        
        <div class="form-actions">
          <button class="btn btn-primary" @click="handleRetry">查询</button>
          <button class="btn btn-secondary" @click="resetForm">重置</button>
        </div>
        
        <div class="examples-section">
          <h3>示例数据</h3>
          <div class="example-items">
            <div class="example-item" @click="fillExample(1)" title="点击填入示例数据到表单">
              <div class="example-label">示例1:</div>
              <div class="example-details">
                <div class="example-row">
                  <span class="example-key">合约地址:</span>
                  <span class="example-value">0xe5C3eE4F7947A722A7E68cb64aed3F9AE6926a39</span>
                </div>
                <div class="example-row">
                  <span class="example-key">数据哈希:</span>
                  <span class="example-value">0xf5ba1479353393150f809e2b4112fe5836f1ea68b81a43e62a40ab4cfead286e</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="query-tips">
          <h3>使用说明</h3>
          <ul>
            <li>确权合约地址为区块链上用于数据确权的智能合约部署地址</li>
            <li>数据哈希值是通过密码学算法生成的数据唯一标识符</li>
            <li>两项信息必须准确填写才能查询到对应的确权证书</li>
            <li>可通过系统管理员获取确权合约地址</li>
          </ul>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { dataEvidenceApi } from '../../plugins/dataEvidence';
import RedSeal from '@/components/RedSeal.vue'; // 导入RedSeal组件
import QrcodeVue from 'qrcode.vue'; // 导入 qrcode.vue 组件

export default {
  name: 'DataCertificate',
  components: { // 注册RedSeal组件
    RedSeal,
    QrcodeVue, // 注册 qrcode.vue 组件
  },
 
  data() {
    return {
      certificateSeal: '碳链网格认证', // 新增：用于存储URL参数id
      // 示例数据，实际应用中应通过API根据ID获取
      certificateData: {
        evidenceId: '', // 自生成的证书编号
        dataHash: '', // bytes32 dataHash - 数据哈希
        submitter: '', // address submitter - 提交者地址
        timestamp: 0, // uint256 timestamp - 时间戳 
        dataType: '', // string dataType - 数据类型
        metadata: '', // string metadata - 元数据（JSON字符串）
        isRevoked: false, // bool isRevoked - 是否撤销
        blockHeight: '', // uint256 blockNumber - 区块号
        blockHash: '', // bytes32 blockHash - 区块哈希
      },
      isLoading: true,
      dynamicRedSealSize: 150, // 默认桌面尺寸
      dynamicQrCodeSize: 80,  // 默认桌面尺寸
      dataHash: '', // 新增：用于存储URL参数dataHash
      contractAddress: '', // 新增：用于存储URL参数contractAddress
      notFound: false, // 新增：证书不存在标志
      contractAddressInput: '', // 新增：表单输入合约地址
      dataHashInput: '', // 新增：表单输入dataHash
      errorMsg: '', // 新增：错误提示
      currentContractAddressForUrl: '', // 新增：用于为当前证书生成URL的合约地址
    };
  },
  computed: {
    currentCertificateUrl() {
      // 确保 certificateData 已加载并且包含必要的字段，同时 currentContractAddressForUrl 也已设置
      if (this.certificateData.dataHash && this.currentContractAddressForUrl) {
        // 使用 Vue Router 解析当前路由的基本路径，以确保与应用的路由模式（hash/history）兼容
        const resolvedRoute = this.$router.resolve({ path: this.$route.path });
        // 构建不含查询参数的基础应用URL
        const baseAppUrl = window.location.origin + resolvedRoute.href;
        
        // 对查询参数进行编码
        const dataHashParam = encodeURIComponent(this.certificateData.dataHash);
        const contractAddressParam = encodeURIComponent(this.currentContractAddressForUrl);
        console.log(`${baseAppUrl}?dataHash=${dataHashParam}&contractAddress=${contractAddressParam}`);
        
        // 返回包含 dataHash 和 contractAddress 的完整URL
        return `${baseAppUrl}?dataHash=${dataHashParam}&contractAddress=${contractAddressParam}`;
      }
      // 如果数据尚未准备好，则回退到当前的 window.location.href。
      // 注意：由于二维码的显示受 v-if="certificateData.evidenceId" 控制，
      // 在 certificateData.evidenceId 为空时，二维码不会使用此回退URL渲染。
      return window.location.href;
    },
    showCertificate() {
      return !this.isLoading && !this.notFound && this.certificateData.evidenceId;
    }
  },
  created() {
    this.isLoading = false;
    // 新增：从URL中获取dataHash和contractAddress
    this.dataHash = this.$route.query.dataHash || '';
    this.contractAddress = this.$route.query.contractAddress || '';
    if (this.dataHash && this.contractAddress) {
      this.fetchCertificateData(this.id, this.dataHash, this.contractAddress);
    }
  },
  mounted() {
    this.updateComponentSizes();
    window.addEventListener('resize', this.updateComponentSizes);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateComponentSizes);
  },
  methods: {
    updateComponentSizes() {
      const width = window.innerWidth;
      if (width <= 480) {
        this.dynamicRedSealSize = 120;
        this.dynamicQrCodeSize = 10;
      } else if (width <= 768) {
        this.dynamicRedSealSize = 120;
        this.dynamicQrCodeSize = 50;
      } else if (width <= 1024) {
        this.dynamicRedSealSize = 120;
        this.dynamicQrCodeSize = 50;
      } else {
        this.dynamicRedSealSize = 120;
        this.dynamicQrCodeSize = 80;
      }
    },
    fetchCertificateData(certificateId, dataHash, contractAddress) {
      this.isLoading = true;
      this.notFound = false;
      this.errorMsg = '';
      const api = dataEvidenceApi(null, contractAddress, '');
      api.getEvidenceByDataHash(dataHash)
        .then(evidence => {
          this.certificateData = {
            evidenceId: evidence.evidenceId,
            dataHash: dataHash,
            submitter: evidence.submitter,
            timestamp: evidence.timestamp,
            dataType: evidence.dataType,
            metadata: evidence.metadata,
            isRevoked: evidence.isRevoked,
            blockHeight: evidence.blockNumber,
            blockHash: evidence.blockHash
          };
          this.currentContractAddressForUrl = contractAddress; // 存储用于获取此证书的合约地址
          this.notFound = false;
        })
        .catch(err => {
          this.certificateData = {
            evidenceId: certificateId || '',
            dataHash: dataHash || '',
            submitter: '',
            timestamp: 0,
            dataType: '',
            metadata: '',
            isRevoked: false,
            blockHeight: '',
            blockHash: ''
          };
          this.notFound = true;
          this.contractAddressInput = contractAddress;
          this.dataHashInput = dataHash;
          this.errorMsg = '证书不存在';
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    handleRetry() {
      this.errorMsg = ''; // 重置错误信息

      const contractAddress = this.contractAddressInput.trim();
      const dataHash = this.dataHashInput.trim();

      if (!contractAddress && !dataHash) {
        this.errorMsg = '确权合约地址和数据哈希值均不能为空';
      } else if (!contractAddress) {
        this.errorMsg = '确权合约地址不能为空';
      } else if (!dataHash) {
        this.errorMsg = '数据哈希值不能为空';
      }

      if (this.errorMsg) {
        // 如果有错误信息，则显示它并且不继续执行查询
        // UI库的提示可以放在这里，例如 this.$message.error(this.errorMsg);
        return;
      }

      // 只有当没有错误信息时（即所有字段都已填写），才执行查询
      this.fetchCertificateData('', dataHash, contractAddress);
    },
    resetForm() {
      this.contractAddressInput = '';
      this.dataHashInput = '';
      this.errorMsg = '';
    },
    formatDate(unixTimestamp, format = null) {
      if (!unixTimestamp) return 'N/A';
      // Assuming unixTimestamp is in seconds
      const date = new Date(unixTimestamp / 1000000 );
      if (!format) {
        const options = { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' };
        return date.toLocaleDateString('zh-CN', options);
      }
      // 简单格式化实现
      const pad = (n) => n < 10 ? '0' + n : n;
      const map = {
        'yyyy': date.getFullYear(),
        'MM': pad(date.getMonth() + 1),
        'dd': pad(date.getDate()),
        'HH': pad(date.getHours()),
        'mm': pad(date.getMinutes()),
        'ss': pad(date.getSeconds()),
      };
      let result = format;
      Object.keys(map).forEach(k => {
        result = result.replace(k, map[k]);
      });
      return result;
    },
    copyToClipboard(text) {
      if (!text) return;
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message && this.$message.success('已复制到剪贴板');
        });
      } else {
        // 兼容旧浏览器
        const textarea = document.createElement('textarea');
        textarea.value = text;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
        this.$message && this.$message.success('已复制到剪贴板');
      }
    },
    fillExample(exampleIndex) {
      // 示例数据
      const examples = [
        {
          contractAddress: '0xe5C3eE4F7947A722A7E68cb64aed3F9AE6926a39',
          dataHash: '0xf5ba1479353393150f809e2b4112fe5836f1ea68b81a43e62a40ab4cfead286e'
        }
      ];
      
      // 索引从1开始，数组从0开始
      const index = exampleIndex - 1;
      if (index >= 0 && index < examples.length) {
        this.contractAddressInput = examples[index].contractAddress;
        this.dataHashInput = examples[index].dataHash;
        this.$message && this.$message.success('已填入示例数据');
      }
    },
  }
};
</script>

<style scoped>
.certificate-page {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: 20px;
}

.certificate-container {
  width: 100%;

  max-width: 450px; /* A4 纸张横向大约 29.7cm，这里取一个适合屏幕的值 */
  aspect-ratio: 1.414 / 1; /* A4 纸张比例 (sqrt(2):1) landscape*/
  /* 如果背景图本身是固定比例，可以调整 aspect-ratio 或通过 padding-bottom hack 来维持比例 */
  /* background-image: url('../../assets/img/cert-bg.jpg'); -- MOVED */
  /* background-size: contain; -- MOVED */
  /* background-repeat: no-repeat; -- MOVED */
  /* background-position: center; -- MOVED */

  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center; /* 尝试将内容垂直居中于背景图的视觉中心 */
  align-items: center;
  box-sizing: border-box;
}

/* 新增：用于控制背景图片显示的类 */
.certificate-container.has-background {

  background-image: url('../../assets/img/cert-bg.jpg');
  background-size: contain; /* contain 或 cover，取决于背景图和内容区域的设计 */
  background-repeat: no-repeat;
  background-position: center;
}

.certificate-content {
  width: 100%; /* 内容区域宽度，相对于 .certificate-container */
  max-width: 100%;
  padding: 20px;
  height: auto;
  color: #333;
  font-family: 'SimSun', 'Songti SC', serif; /* 证书常用宋体类字体 */
  text-align: center; /* 整体内容居中 */
  background-color: rgba(255, 255, 255, 0.1); /* 轻微透明背景，使文字更易读，可选 */
  box-sizing: border-box;
  border-radius: 4px;
  position: relative; /* 为绝对定位的子元素提供参照 */
}

.certificate-header h1 {
  font-size: 2em; /* 动态调整 */
  color: #0d47a1; /* 深蓝色，可根据背景图调整 */
  margin-bottom: 20px;
  font-weight: bold;
  margin-top: 20px;
}

.certificate-body {
  padding: 0 40px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); /* 响应式网格 */
  gap: 5px; /* 网格间距 */
  text-align: left; /* 项目内部文本左对齐 */
}

.info-item {
  padding: 4px 0;
  border-bottom: 1px solid #eee; /* 轻微分割线 */
}

.info-item.full-width {
  grid-column: 1 / -1; /* 占据整行 */
}

.info-item .label {
  font-weight: bold;
  color: #424242;
  margin-right: 8px;
  display: block; /* 标签和值分行显示，更清晰 */
  margin-bottom: 4px;
}

.info-item .value {
  color: #555;
  word-wrap: break-word; /* 长文本换行 */
}
.value.key-value {
  color: #000; /* Black for key values to stand out */
}
.value.data-description {
  max-height: 100px; /* 限制描述的最大高度 */
  overflow-y: auto; /* 如果内容超出则滚动 */
  display: block;
  font-size: 0.9em;
  line-height: 1.6;
  text-align: justify;
}

.value-metadata {
  white-space: pre-wrap;
  word-break: break-all;
  max-height: 100px;
  overflow-y: auto;
  display: block;
  background-color: #f8f9fa;
  padding: 8px;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
  font-size: 0.85em; /* Smaller for dense info, matches user's data-description style */
  line-height: 1.5;
  text-align: left; /* Align JSON content to the left */
}

.status-normal {
  color: #28a745; /* Green */
  font-weight: bold;
}
.status-revoked {
  color: #dc3545; /* Red */
  font-weight: bold;
}

.certificate-footer {
  font-size: 0.8em;
  color: #757575;
  position: relative; /* 确保页脚可以作为定位上下文，尽管图章可能相对于更高层级的元素定位 */
  margin-bottom: 15px;
}

.certificate-footer p.footer-text {
  margin: 0 auto; /* 使段落居中 */
  max-width: 90%; /* 避免文本过宽 */
}

.qr-code {
  display: block;
 
}

/* 新增：为红章定位的样式 */
.certificate-seal {
  position: absolute;
  right: 40px; /* 距离右边框的距离 */
  bottom: 90px; /* 距离底部边框的距离 */
  /* 根据实际图章大小和证书设计调整这些值 */
  /* width: 100px; */ /* 由 size prop 控制 */
  /* height: 100px; */ /* 由 size prop 控制 */
  transform: scale(0.8); /* 可选：稍微缩小图章 */
  transform-origin: bottom right;
}

.footer-content-wrapper {
  text-align: center; /* 内部元素居中 */
}

.footer-qr {
  display: block;
  /* width: 80px; */ /* 由 size prop 控制 */
  /* height: 80px; */ /* 由 size prop 控制 */
  margin: 0 auto 4px auto; /* 居中并与下方文字保持间距 */
}




.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 300px; /* 根据证书大致高度给一个初始高度，减少晃动 */
  color: #555;
  font-weight: bold;
  font-size: 1.8em;
}

/* 为加载文字添加动画 */
.loading-container p {
  animation: pulse 1.5s infinite ease-in-out;
}

/* 定义脉冲动画 */
@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

/* 响应式调整 */
@media (max-width: 1024px) {
  .certificate-content {
    width: 55%; /* 内容区域宽度，相对于 .certificate-container */
    max-width: 55%;
  }

  .certificate-body {
    margin-bottom: 10px;
    padding: 0 10px;
  }
}

/* Styles for tablet portrait and small desktops */
@media (max-width: 768px) {
  .certificate-header h1 {
    font-size: 1.8em;
  }
  .info-item .label, .info-item .value {
    font-size: 0.95em;
  }
  .certificate-body {
    padding: 0 20px; /* Adjust padding for tablets */
  }
  .certificate-seal {
    right: 20px; 
    bottom: 70px;
    transform: scale(0.7);
  }
}

/* Styles for mobile devices */
@media (max-width: 480px) {
  
  .certificate-page {
    padding: 10px; /* Reduce page padding */
  }
  .certificate-container.has-background {
    height: 100%;
    min-height: 100vh;
  }
  
  .certificate-content {
    width: 100%; /* Full width on mobile */
    max-width: 100%;
    padding: 25px; /* Adjust content padding */
  }
  .certificate-header h1 {
    font-size: 1.5em; /* Smaller title */
    margin-top: 10px;
    margin-bottom: 5px;
  }
  .certificate-body {
    margin-bottom: 0px;
  }
  .info-item {
    padding: 2px 0; /* Slightly reduce item padding */
  }
  .info-item .label, .info-item .value {
    font-size: 0.6em; /* Smaller text for info items */
  }
  .value.key-value {
     /* Will inherit 0.9em, color: #000 is fine */
  }
  .value-metadata {
    font-size: 0.8em; /* Even smaller for dense metadata */
    max-height: 20px; /* Reduce max height */
    padding: 6px;
  }
  .status-normal, .status-revoked {
    /* Will inherit 0.9em and bold, color will apply */
  }
  .certificate-footer {
    font-size: 0.75em; /* Smaller footer text */
    margin-bottom: 10px;
  }
  .footer-content-wrapper {
    text-align: left; /* Align footer content to left */
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding: 0 20px
  }
  .footer-qr {
    margin: 0 0 8px 0; /* Align QR to left and add bottom margin */
     /* Size handled by JS: dynamicQrCodeSize = 40 */
  }
  .certificate-footer p.footer-text {
    font-size: 0.95em; /* Relative to parent footer font-size */
    text-align: left;
    margin-left: 0px;
    max-width: 100%;
  }
  .certificate-seal {
    right: 10px;
    bottom: 15px; /* Adjust seal position for mobile */
    transform: scale(0.6); /* Further scale down if needed */
     /* Size handled by JS: dynamicRedSealSize = 80 */
  }
}

/* 数据确权证书查询门户样式 */
.main-query-container {
  width: 100%;
  max-width: 600px;
  margin: 0 auto;
  padding: 20px;
  box-sizing: border-box;
}

.query-portal {
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  padding: 30px;
  width: 100%;
  box-sizing: border-box;
  max-width: 100%;
}

.query-title {
  color: #0d47a1;
  font-size: 1.8em;
  margin-bottom: 15px;
  text-align: left;
}

.query-description {
  color: #666;
  margin-bottom: 25px;
  text-align: left;
}

.error-message {
  background-color: #fff8f8;
  color: #d32f2f;
  padding: 10px;
  border-radius: 4px;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.error-icon {
  margin-right: 8px;
}

.form-group {
  margin-bottom: 20px;
  text-align: left;
  width: 100%;
  box-sizing: border-box;
}

.form-group label {
  display: block;
  font-weight: bold;
  margin-bottom: 8px;
  color: #333;
  font-size: 1.2em;
}

.form-control {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1em;
  transition: border-color 0.3s;
  box-sizing: border-box;
  max-width: 100%;
  overflow: hidden;
}

.form-control:focus {
  border-color: #0d47a1;
  outline: none;
  box-shadow: 0 0 0 2px rgba(13, 71, 161, 0.2);
}

.form-actions {
  display: flex;
  justify-content: flex-start;
  gap: 12px;
  margin-top: 30px;
  margin-bottom: 25px;
}

.btn {
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 1em;
  cursor: pointer;
  border: none;
  transition: background-color 0.3s, transform 0.1s;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn:active {
  transform: translateY(1px);
}

.btn-primary {
  background-color: #0d47a1;
  color: white;
}

.btn-primary:hover {
  background-color: #0a3b7d;
}

.btn-secondary {
  background-color: #e0e0e0;
  color: #333;
}

.btn-secondary:hover {
  background-color: #d5d5d5;
}

.examples-section {
  margin: 20px 0;
  background-color: #f5f8ff;
  padding: 15px;
  border-radius: 4px;
}

.examples-section h3 {
  font-size: 1.1em;
  margin-bottom: 10px;
  color: #0d47a1;
  text-align: left;
}

.example-items {
  /* 此容器主要用于包裹多个example-item，可能不需要特定样式了 */
  /* 如果需要控制多个示例项的间距，可以在 .example-item 上使用 margin-bottom */
}

.example-item {
  padding: 5px;
  color: #333;
  background-color: transparent;
  border: none;
  text-align: left; /* 控制内部所有文本左对齐 */
  margin-bottom: 15px; /* 重新添加，用于分隔不同的示例项 */
  cursor: pointer; /* 添加鼠标指针样式 */
  transition: background-color 0.2s ease-in-out; /* 轻微的悬停反馈 */
}

.example-item:hover {
  background-color: rgba(0, 0, 0, 0.03); /* 鼠标悬停时的轻微背景变化 */
}

.example-item:last-child {
  margin-bottom: 0; /* 最后一个示例项不需要底部外边距 */
}

.example-label {
  display: block;
  font-weight: bold;
  margin-bottom: 8px;
  /* color: #333; -- 继承自 .example-item */
  font-size: 1.05em;
}

.example-details {
  /* 此类可能不再需要，如果HTML中还存在但无样式，可以考虑移除或保留为空 */
}

.example-row {
  display: flex;
  align-items: flex-start;
  margin-bottom: 6px;
  line-height: 1.5;
}

.example-key {
  font-weight: bold;
  color: #555;
  font-size: 0.95em;
  margin-right: 8px;
  flex-shrink: 0;
}

.example-value {
  font-family: monospace;
  background-color: #f8f9fa;
  padding: 2px 6px;
  border-radius: 3px;
  border: 1px solid #eee;
  font-size: 0.9em;
  word-break: break-all;
  /* margin-left: 0; -- 默认值，且flex布局下意义不大 */
  flex-grow: 1;
  /* text-align: left; -- 继承自 .example-item */
}

/* 响应式调整 */
@media (min-width: 500px) {
  .example-key {
     min-width: 75px;
  }
}

@media (max-width: 499px) {
  .example-row {
    flex-direction: column;
    align-items: flex-start;
    line-height: normal;
  }
  .example-key {
    margin-right: 0;
    margin-bottom: 4px;
    min-width: auto;
  }
  .example-value {
    width: 100%;
    padding: 6px 8px;
  }
}

.query-tips {
  background-color: #f5f8ff;
  padding: 15px;
  border-radius: 4px;
  margin-top: 20px;
  text-align: left;
}

.query-tips h3 {
  font-size: 1.1em;
  margin-bottom: 10px;
  color: #0d47a1;
  text-align: left;
}

.query-tips ul {
  padding-left: 20px;
  margin: 0;
  text-align: left;
}

.query-tips li {
  margin-bottom: 8px;
  color: #555;
  text-align: left;
}

.btn-fill-example {
  background-color: #0d47a1;
  color: white;
  padding: 8px 15px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
}

.btn-fill-example:hover {
  background-color: #0a3b7d;
}

/* 响应式样式调整 */
@media (max-width: 768px) {
  .query-portal {
    padding: 20px;
  }
  
  .query-title {
    font-size: 1.5em;
  }
  
  .form-actions {
    flex-direction: column;
    gap: 10px;
  }
  
  .btn {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .main-query-container {
    padding: 10px;
  }
  
  .query-portal {
    padding: 15px;
  }
  
  .query-title {
    font-size: 1.3em;
  }
  
  .query-description {
    font-size: 0.9em;
  }
}
</style> 